import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-shadow duration-300"
      style={{ background: '#1A1A1A', boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.4)' : 'none' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex flex-col leading-none flex-shrink-0">
            <span style={{ fontFamily: '"Barlow Condensed", sans-serif', color: '#F5C300', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '0.08em' }}>
              NORTH CITY
            </span>
            <span style={{ fontFamily: '"Barlow Condensed", sans-serif', color: '#ffffff', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.18em' }}>
              SERVICE &amp; TYRE CENTRE
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  color: location.pathname === link.to ? '#F5C300' : '#ffffff',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => { if (location.pathname !== link.to) e.target.style.color = '#F5C300'; }}
                onMouseLeave={e => { if (location.pathname !== link.to) e.target.style.color = '#ffffff'; }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Book Now CTA */}
          <div className="hidden md:flex">
            <Link
              to="/contact"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#F5C300',
                color: '#1A1A1A',
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: '0.08em',
                padding: '0.6rem 1.5rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                transition: 'background 0.2s, transform 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              BOOK NOW
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 focus:outline-none"
            aria-label="Toggle navigation menu"
            style={{ color: '#ffffff' }}
          >
            <span
              style={{
                display: 'block', width: '24px', height: '2px', background: '#fff',
                marginBottom: '5px', transition: 'all 0.3s',
                transform: isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
              }}
            />
            <span
              style={{
                display: 'block', width: '24px', height: '2px', background: '#fff',
                marginBottom: '5px', transition: 'all 0.3s',
                opacity: isOpen ? 0 : 1,
              }}
            />
            <span
              style={{
                display: 'block', width: '24px', height: '2px', background: '#fff',
                transition: 'all 0.3s',
                transform: isOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ background: '#1A1A1A', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {links.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: location.pathname === link.to ? '#F5C300' : '#ffffff',
                    textDecoration: 'none',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  background: '#F5C300', color: '#1A1A1A',
                  fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '0.95rem',
                  letterSpacing: '0.08em', padding: '0.75rem 1.5rem', borderRadius: '9999px',
                  textDecoration: 'none', marginTop: '0.5rem',
                }}
              >
                BOOK NOW
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
